import { forwardRef, memo, type HTMLAttributes } from 'react';
import { cva, type CVAVariantProps } from '../../utils/variants';
import { cn } from '../../utils/cn';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti del Divider
 */
const dividerVariants = cva(
  'border-gray-200',
  {
    variants: {
      orientation: {
        horizontal: 'w-full border-t',
        vertical: 'h-full border-l',
      },
      variant: {
        solid: 'border-solid',
        dashed: 'border-dashed',
        dotted: 'border-dotted',
      },
      spacing: {
        none: '',
        sm: '',
        md: '',
        lg: '',
      },
    },
    compoundVariants: [
      {
        orientation: 'horizontal',
        spacing: 'sm',
        className: 'my-2',
      },
      {
        orientation: 'horizontal',
        spacing: 'md',
        className: 'my-4',
      },
      {
        orientation: 'horizontal',
        spacing: 'lg',
        className: 'my-8',
      },
      {
        orientation: 'vertical',
        spacing: 'sm',
        className: 'mx-2',
      },
      {
        orientation: 'vertical',
        spacing: 'md',
        className: 'mx-4',
      },
      {
        orientation: 'vertical',
        spacing: 'lg',
        className: 'mx-8',
      },
    ],
    defaultVariants: {
      orientation: 'horizontal',
      variant: 'solid',
      spacing: 'md',
    },
  }
);

export interface DividerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'>,
    BaseComponentProps,
    CVAVariantProps<typeof dividerVariants> {
  /**
   * Testo da mostrare nel divider
   */
  label?: string;
  /**
   * Posizione del testo
   */
  labelPosition?: 'left' | 'center' | 'right';
}

/**
 * Componente Divider
 * 
 * Separatore visivo orizzontale o verticale, opzionalmente con testo.
 * 
 * @example
 * ```tsx
 * <Divider />
 * <Divider variant="dashed" />
 * <Divider label="oppure" labelPosition="center" />
 * <Divider orientation="vertical" />
 * ```
 */
const DividerComponent = forwardRef<HTMLDivElement, DividerProps>(
  (
    {
      className,
      orientation,
      variant,
      spacing,
      label,
      labelPosition = 'center',
      ...props
    },
    ref
  ) => {
    // Se c'è un label, usa un layout speciale
    if (label && orientation === 'horizontal') {
      const positionClasses = {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end',
      };

      return (
        <div
          ref={ref}
          className={cn(
            'flex items-center',
            spacing === 'sm' && 'my-2',
            spacing === 'md' && 'my-4',
            spacing === 'lg' && 'my-8',
            positionClasses[labelPosition],
            className
          )}
          role="separator"
          {...props}
        >
          {labelPosition !== 'left' && (
            <div className={cn('flex-1', dividerVariants({ orientation, variant, spacing: 'none' }))} />
          )}
          <span className="px-3 text-sm text-gray-500 whitespace-nowrap">
            {label}
          </span>
          {labelPosition !== 'right' && (
            <div className={cn('flex-1', dividerVariants({ orientation, variant, spacing: 'none' }))} />
          )}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(dividerVariants({ orientation, variant, spacing }), className)}
        role="separator"
        aria-orientation={orientation || undefined}
        {...props}
      />
    );
  }
);

DividerComponent.displayName = 'Divider';

export const Divider = memo(DividerComponent);

